import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRealtime } from '@/context/RealtimeContext';
import { dashboardApi } from '@/api/dashboard.api';
import { shopifyApi } from '@/api/shopify.api';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { CxBadge } from '@/components/tickets/badges';
import { useAuth } from '@/context/AuthContext';
import { TicketTable } from '@/components/tickets/TicketTable';
import { ViewType } from '@/types/domain';
import { formatHoursCompact } from '@/lib/format';
import { viewTypeLabel } from '@/lib/labels';

/**
 * V4.9 — Dashboard redesigned around a tab strip instead of one long
 * vertical scroll. The old layout stacked 6 cards full-width — admins
 * had to scroll through 4-5 screen-heights of content to reach Pending
 * Approvals. Now everything fits in one viewport:
 *
 *   - KPI cards row (always visible, top fold)
 *   - Tab strip with per-tab counts so admins see what needs attention
 *     without flipping (e.g. "Pending Approvals · 5")
 *   - Active tab body fills the remaining screen
 *
 * Default tab = My Queue (the most actionable view).
 */
type DashboardTab = 'queue' | 'workload' | 'approvals' | 'dispute' | 'cx' | 'shopify';

export function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.view_type === ViewType.Admin;
  const showShopify = isAdmin || user?.view_type === ViewType.Specialist;

  const [tab, setTab] = useState<DashboardTab>('queue');

  const statsQuery = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => dashboardApi.stats()
  });
  const disputeQuery = useQuery({
    queryKey: ['dashboard', 'disputeRisk'],
    queryFn: () => dashboardApi.disputeRisk()
  });
  const workloadQuery = useQuery({
    queryKey: ['dashboard', 'workload'],
    queryFn: () => dashboardApi.workload()
  });
  const cxWatchQuery = useQuery({
    queryKey: ['dashboard', 'cxWatch'],
    queryFn: () => dashboardApi.cxWatch()
  });
  const myQueueQuery = useQuery({
    queryKey: ['dashboard', 'myQueue'],
    queryFn: () => dashboardApi.myQueue()
  });
  const approvalsQuery = useQuery({
    queryKey: ['dashboard', 'approvals'],
    queryFn: () => dashboardApi.approvals(),
    enabled: isAdmin
  });
  const shopifyHealthQuery = useQuery({
    queryKey: ['dashboard', 'shopify-health'],
    queryFn: () => shopifyApi.webhookSummary(),
    enabled: showShopify,
    refetchInterval: 60_000
  });
  // V4.9.10 — Per-geo breakdown row (Item #4). 4 buckets shown as cards
  // between the top stats and the tabs. Click → /tickets?store=...
  const geoQuery = useQuery({
    queryKey: ['dashboard', 'geo-breakdown'],
    queryFn: () => dashboardApi.geoBreakdown()
  });

  // V4.9.5 — Live dashboard refresh. Any ticket created/changed anywhere
  // in the system invalidates every dashboard query so the stat cards +
  // queue tables update within ~200ms of the event landing.
  const queryClient = useQueryClient();
  const { on } = useRealtime();
  useEffect(() => {
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    const off1 = on('ticket.created', invalidate);
    const off2 = on('ticket.status_changed', invalidate);
    const off3 = on('ticket.updated', invalidate);
    const off4 = on('ticket.approval_decided', invalidate);
    const off5 = on('ticket.cx_informed', invalidate);
    return () => {
      off1();
      off2();
      off3();
      off4();
      off5();
    };
  }, [on, queryClient]);

  const tabs: Array<{ id: DashboardTab; label: string; count: number; tone?: 'warning' | 'danger'; visible: boolean }> = [
    {
      id: 'queue',
      label: 'My queue',
      count: myQueueQuery.data?.length ?? 0,
      visible: true
    },
    {
      id: 'workload',
      label: 'Workload',
      count: workloadQuery.data?.length ?? 0,
      visible: true
    },
    {
      id: 'approvals',
      label: 'Pending approvals',
      count: approvalsQuery.data?.length ?? 0,
      tone: (approvalsQuery.data?.length ?? 0) > 0 ? 'warning' : undefined,
      visible: isAdmin
    },
    {
      id: 'dispute',
      label: 'Dispute risk',
      count: disputeQuery.data?.length ?? 0,
      tone: (disputeQuery.data?.length ?? 0) > 0 ? 'danger' : undefined,
      visible: true
    },
    {
      id: 'cx',
      label: 'CX silence',
      count: cxWatchQuery.data?.length ?? 0,
      tone: (cxWatchQuery.data?.length ?? 0) > 0 ? 'warning' : undefined,
      visible: true
    },
    {
      id: 'shopify',
      label: 'Shopify health',
      count: shopifyHealthQuery.data?.length ?? 0,
      visible: showShopify
    }
  ];

  return (
    <div>
      <div className="section-header">
        <h2>Welcome, {user?.name.split(' ')[0]}</h2>
        <div className="text-muted text-small">
          Signed in as {user?.name} · {user?.view_type ? viewTypeLabel(user.view_type) : ''}
        </div>
      </div>

      <StatsRow
        loading={statsQuery.isLoading}
        error={statsQuery.error}
        data={statsQuery.data}
      />

      {/* V4.9.10 — Per-geo breakdown cards (Item #4). Sits between the
         top stats and the tabs per ops preference. Click → All Tickets
         filtered by that geo's store codes. */}
      <div className="geo-breakdown-grid mt-6">
        {(geoQuery.data ?? []).map((bucket) => {
          // V4.9.12 — Each metric tile is now its OWN drill-down link that
          // applies BOTH the region (store) filter AND the metric's filter,
          // so clicking "Breached" shows only this region's breached tickets
          // — not every ticket for the store. (Previously the whole card was
          // one link to `?store=…`, which returned everything.) All three use
          // `open=1` to match the card's open-work counts; Breached adds
          // breach_flag=BREACHED, Approvals adds approval_state=Waiting Admin.
          const store = bucket.store_codes.join(',');
          return (
            <div key={bucket.geo} className="geo-card">
              <div className="geo-card-header">{bucket.label}</div>
              <div className="geo-card-stats">
                <Link
                  to={`/tickets?store=${store}&open=1`}
                  className="geo-stat"
                  title={`Open tickets — ${bucket.label}`}
                >
                  <div className="geo-card-num">{bucket.open}</div>
                  <div className="geo-card-sub">Open</div>
                </Link>
                <Link
                  to={`/tickets?store=${store}&open=1&breach_flag=BREACHED`}
                  className="geo-stat"
                  title={`Breached tickets — ${bucket.label}`}
                >
                  <div className={`geo-card-num ${bucket.breached > 0 ? 'is-danger' : ''}`}>
                    {bucket.breached}
                  </div>
                  <div className="geo-card-sub">Breached</div>
                </Link>
                <Link
                  to={`/tickets?store=${store}&open=1&approval_state=${encodeURIComponent('Waiting Admin')}`}
                  className="geo-stat"
                  title={`Awaiting approval — ${bucket.label}`}
                >
                  <div className={`geo-card-num ${bucket.approvals_waiting > 0 ? 'is-warning' : ''}`}>
                    {bucket.approvals_waiting}
                  </div>
                  <div className="geo-card-sub">Approvals</div>
                </Link>
              </div>
              <Link to={`/tickets?store=${store}`} className="geo-card-cta">
                See all →
              </Link>
            </div>
          );
        })}
        {geoQuery.isLoading
          ? [1, 2, 3, 4].map((n) => (
              <div key={n} className="geo-card geo-card-loading">
                <div className="geo-card-header">Loading…</div>
              </div>
            ))
          : null}
      </div>

      {/* V4.9 — Tab strip replaces the previous 6-card vertical stack. */}
      <div className="dashboard-tabs mt-6">
        {tabs.filter((t) => t.visible).map((t) => (
          <button
            key={t.id}
            type="button"
            className={`dashboard-tab ${tab === t.id ? 'is-active' : ''} ${t.tone ? `tab-tone-${t.tone}` : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span className="dashboard-tab-label">{t.label}</span>
            {t.count > 0 ? (
              <span className={`dashboard-tab-count ${t.tone ? `count-tone-${t.tone}` : ''}`}>
                {t.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="dashboard-tab-body">
        {tab === 'queue' ? (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">My queue</h3>
              <Link to="/my-queue" className="btn btn-link">
                See all →
              </Link>
            </div>
            <div className="card-body no-pad">
              {myQueueQuery.isLoading ? (
                <LoadingState />
              ) : myQueueQuery.isError ? (
                <ErrorState error={myQueueQuery.error} onRetry={() => myQueueQuery.refetch()} />
              ) : (
                <TicketTable
                  tickets={(myQueueQuery.data ?? []).slice(0, 10)}
                  emptyLabel="Your queue is empty. Great work!"
                />
              )}
            </div>
          </div>
        ) : null}

        {tab === 'workload' ? (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Team workload</h3>
              <span className="text-muted text-small">Open tickets per teammate vs. their cap.</span>
            </div>
            <div className="card-body no-pad">
              {workloadQuery.isLoading ? (
                <LoadingState />
              ) : workloadQuery.isError ? (
                <ErrorState error={workloadQuery.error} onRetry={() => workloadQuery.refetch()} />
              ) : (workloadQuery.data ?? []).length === 0 ? (
                <EmptyState title="No workload data" />
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th>Role</th>
                        <th>Open</th>
                        <th>Max</th>
                        <th>Load</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(workloadQuery.data ?? []).map((row) => {
                        // V4.9.5+ — prefer the normalized "Sales Lead — UK" label
                        // (built from view_type + geo) over the legacy free-text
                        // user.role field which was inconsistent across rows.
                        const roleDisplay = row.role_label ?? row.role;
                        // Bogus caps (9999, 99999) come back as effective_max=null
                        // from the backend; render "—" + a load bucket instead of
                        // pretending we have a real percentage.
                        const hasRealCap = row.effective_max != null && row.pct_of_max != null;
                        const bucket = hasRealCap ? null : loadBucket(row.currently_open);
                        return (
                          <tr key={row.person_id} className="no-hover">
                            <td>
                              <div>{row.person_name}</div>
                              {row.breached > 0 ? (
                                <Badge tone="danger">{row.breached} breached</Badge>
                              ) : null}
                            </td>
                            <td className="text-small">{roleDisplay}</td>
                            <td>{row.currently_open}</td>
                            <td>
                              {hasRealCap ? (
                                row.effective_max
                              ) : (
                                <span
                                  className="text-muted"
                                  title={`No cap set (DB value: ${row.max_open})`}
                                >
                                  —
                                </span>
                              )}
                            </td>
                            <td>
                              {hasRealCap ? (
                                <Badge tone={loadTone(row.pct_of_max!)}>
                                  {row.pct_of_max}%
                                </Badge>
                              ) : (
                                <Badge tone={bucket!.tone}>{bucket!.label}</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {tab === 'approvals' && isAdmin ? (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Pending approvals</h3>
              <Link to="/approvals" className="btn btn-link">
                See all →
              </Link>
            </div>
            <div className="card-body no-pad">
              {approvalsQuery.isLoading ? (
                <LoadingState />
              ) : approvalsQuery.isError ? (
                <ErrorState error={approvalsQuery.error} onRetry={() => approvalsQuery.refetch()} />
              ) : (approvalsQuery.data ?? []).length === 0 ? (
                <EmptyState title="Nothing waiting" description="All approval requests have been decided." />
              ) : (
                <TicketTable
                  tickets={(approvalsQuery.data ?? []).slice(0, 10)}
                  emptyLabel="No pending approvals"
                />
              )}
            </div>
          </div>
        ) : null}

        {tab === 'dispute' ? (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Dispute risk watch</h3>
              <span className="text-muted text-small">Tickets aging past safe-resolution thresholds.</span>
            </div>
            <div className="card-body no-pad">
              {disputeQuery.isLoading ? (
                <LoadingState />
              ) : disputeQuery.isError ? (
                <ErrorState error={disputeQuery.error} onRetry={() => disputeQuery.refetch()} />
              ) : (disputeQuery.data ?? []).length === 0 ? (
                <EmptyState title="No at-risk tickets" description="Nothing over 24h without resolution." />
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ticket</th>
                        <th>Age</th>
                        <th>Category</th>
                        <th>Severity</th>
                        <th>Customer</th>
                        <th>Owner</th>
                        <th>Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(disputeQuery.data ?? []).map((row) => (
                        <tr key={row.ticket_id} className="no-hover">
                          <td>
                            {/* V4.9.6 — pass from=/dashboard so the Back link
                                on the ticket detail returns the user to the
                                dashboard they came from, not All Tickets. */}
                            <Link
                              className="font-mono"
                              to={`/tickets/${row.ticket_id}`}
                              state={{ from: '/dashboard' }}
                            >
                              {row.ticket_id}
                            </Link>
                          </td>
                          <td>{formatHoursCompact(row.age_hours)}</td>
                          <td>{row.category}</td>
                          <td>{row.severity}</td>
                          <td>{row.customer_name ?? '—'}</td>
                          <td>{row.owner?.name ?? 'Unassigned'}</td>
                          <td>
                            <Badge tone={riskBandTone(row.risk_band)}>{row.risk_band}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {tab === 'cx' ? (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">CX silence watch</h3>
              <span className="text-muted text-small">Tickets where the customer hasn't been updated recently.</span>
            </div>
            <div className="card-body no-pad">
              {cxWatchQuery.isLoading ? (
                <LoadingState />
              ) : cxWatchQuery.isError ? (
                <ErrorState error={cxWatchQuery.error} onRetry={() => cxWatchQuery.refetch()} />
              ) : (cxWatchQuery.data ?? []).length === 0 ? (
                <EmptyState title="Customers are being kept informed" description="No tickets are silent right now." />
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ticket</th>
                        <th>Customer</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>CX status</th>
                        <th>Since CX</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(cxWatchQuery.data ?? []).map((row) => (
                        <tr key={row.ticket_id} className="no-hover">
                          <td>
                            <Link
                              className="font-mono"
                              to={`/tickets/${row.ticket_id}`}
                              state={{ from: '/dashboard' }}
                            >
                              {row.ticket_id}
                            </Link>
                          </td>
                          <td>{row.customer_name ?? '—'}</td>
                          <td>{row.category}</td>
                          <td>{row.status}</td>
                          <td><CxBadge status={row.cx_status} /></td>
                          <td>{formatHoursCompact(row.hrs_since_cx)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {tab === 'shopify' && showShopify ? (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Shopify integration health</h3>
              <Link to="/shopify" className="btn btn-link">
                Manage →
              </Link>
            </div>
            <div className="card-body">
              {shopifyHealthQuery.isLoading ? (
                <LoadingState />
              ) : shopifyHealthQuery.isError ? (
                <ErrorState
                  error={shopifyHealthQuery.error}
                  onRetry={() => shopifyHealthQuery.refetch()}
                />
              ) : (
                <ShopifyHealthMini data={shopifyHealthQuery.data ?? []} />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatsRow({
  loading,
  error,
  data
}: {
  loading: boolean;
  error: unknown;
  data: import('@/types/api').DashboardStats | undefined;
}) {
  if (loading) {
    return (
      <div className="stats-grid">
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="stat-card">
            <div className="stat-label">Loading</div>
            <div className="stat-value">—</div>
          </div>
        ))}
      </div>
    );
  }
  if (error || !data) {
    return <ErrorState error={error ?? new Error('No data')} />;
  }
  return (
    <div className="stats-grid">
      {/* V4.9.12 — `open=1` (not status=Open) so this matches total_open,
         which counts every unresolved ticket — not just the literal "Open"
         status. Clicking the card now lands on the same set it counts. */}
      <StatCard label="Open tickets" value={data.total_open} to="/tickets?open=1" />
      <StatCard label="Created today" value={data.created_today} to="/tickets?created=today" />
      <StatCard
        label="Breached"
        value={data.breached}
        tone={data.breached > 0 ? 'danger' : 'neutral'}
        to="/tickets?breach_flag=BREACHED"
      />
      <StatCard
        label="Due within 24h"
        value={data.under_24h}
        tone={data.under_24h > 0 ? 'warning' : 'neutral'}
        to="/tickets?breach_flag=WARNING_24H"
      />
      <StatCard
        label="Approvals waiting"
        value={data.approvals_waiting}
        tone={data.approvals_waiting > 0 ? 'warning' : 'neutral'}
        to="/approvals"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
  to
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'warning' | 'danger';
  to?: string;
}) {
  const toneClass =
    tone === 'danger' ? ' stat-card-danger' : tone === 'warning' ? ' stat-card-warning' : '';
  const inner = (
    <>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </>
  );
  if (!to) {
    return <div className={`stat-card${toneClass}`}>{inner}</div>;
  }
  return (
    <Link to={to} className={`stat-card stat-card-clickable${toneClass}`}>
      {inner}
    </Link>
  );
}

function riskBandTone(
  band: 'RED' | 'CRITICAL' | 'WARNING' | 'WATCH'
): 'danger' | 'warning' | 'info' | 'neutral' {
  if (band === 'RED' || band === 'CRITICAL') return 'danger';
  if (band === 'WARNING') return 'warning';
  if (band === 'WATCH') return 'info';
  return 'neutral';
}

function loadTone(pct: number): 'danger' | 'warning' | 'info' | 'neutral' {
  if (pct >= 100) return 'danger';
  if (pct >= 80) return 'warning';
  if (pct >= 50) return 'info';
  return 'neutral';
}

/**
 * V4.9.5+ — When a user has no meaningful cap (max_open is set to one of
 * the legacy "unlimited" placeholders like 9999/99999), pct_of_max comes
 * back as null. Instead of showing "0.5%" against a bogus cap, bucket the
 * raw open count into Light / Moderate / Heavy / Overloaded so the
 * dashboard still surfaces who's getting hammered.
 */
function loadBucket(open: number): { label: string; tone: 'danger' | 'warning' | 'info' | 'neutral' } {
  if (open >= 40) return { label: 'Overloaded', tone: 'danger' };
  if (open >= 20) return { label: 'Heavy', tone: 'warning' };
  if (open >= 8) return { label: 'Moderate', tone: 'info' };
  if (open >= 1) return { label: 'Light', tone: 'neutral' };
  return { label: 'Idle', tone: 'neutral' };
}

function ShopifyHealthMini({
  data
}: {
  data: import('@/types/api').WebhookStoreSummary[];
}) {
  if (!data.length) {
    return <EmptyState title="No Shopify stores configured." />;
  }
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Store</th>
          <th>Webhooks</th>
          <th>Last delivery</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row) => {
          const healthy =
            row.has_webhook_secret && row.has_access_token && row.total_webhooks > 0;
          return (
            <tr key={row.code}>
              <td>
                <Badge tone={healthy ? 'success' : 'warning'}>{row.code.toUpperCase()}</Badge>{' '}
                <span className="text-small text-muted">{row.shopify_domain}</span>
              </td>
              <td>{row.total_webhooks}</td>
              <td className="text-small">
                {row.last_webhook_at
                  ? new Date(row.last_webhook_at).toLocaleString()
                  : 'never'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
